import React, { useMemo } from 'react';
import { Rating } from 'react-simple-star-rating';
import { SvgIcon } from 'ui/SvgIcon';
import { ProgressBar } from 'ui/ProgressBar';
import classNames from 'classnames';
import { isNil } from 'ramda';
import {
  foodIcon,
  locationIcon,
  serviceIcon,
  roomsIcon,
  activitiesIcon,
  atmosphereIcon,
  amenitiesIcon,
  comfortIcon,
  experienceIcon,
  cleanlinessIcon,
} from './icons';
import { SeeMoreTextCollapsible } from 'ui/SeeMoreTextCollapsible';

interface IDetailedScores {
  rooms: number | null;
  foodAndBeverages: number | null;
  location: number | null;
  service: number | null;
  cleanliness: number | null;
  activities: number | null;
  atmosphere: number | null;
  amenities: number | null;
  comfort: number | null;
  overallExperience: number | null;
}

interface IReviewSummaryBoxProps {
  generalScore: number | null;
  summaryText: string;
  detailedScores: IDetailedScores;
  hideDescription?: boolean;
  summaryMaxLines?: number;
}

const scoreLabels: Record<string, { label: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }> = {
  rooms: { label: 'Rooms', icon: roomsIcon },
  foodAndBeverages: { label: 'Food', icon: foodIcon },
  location: { label: 'Location', icon: locationIcon },
  service: { label: 'Service', icon: serviceIcon },
  cleanliness: { label: 'Cleanliness', icon: cleanlinessIcon },
  activities: { label: 'Activities', icon: activitiesIcon },
  atmosphere: { label: 'Atmosphere', icon: atmosphereIcon },
  amenities: { label: 'Amenities', icon: amenitiesIcon },
  comfort: { label: 'Comfort', icon: comfortIcon },
  overallExperience: { label: 'Experience', icon: experienceIcon },
};

export const ReviewSummaryBox: React.FC<IReviewSummaryBoxProps> = ({
  generalScore,
  summaryText,
  detailedScores,
  hideDescription = false,
  summaryMaxLines = 10,
}) => {
  const displayedScores = useMemo(() => {
    const ds = ((detailedScores || {}) as unknown) as Record<string, number | null | undefined>;
    return Object.keys(scoreLabels)
      .map(key => ({ key, value: ds[key] }))
      .filter(item => !isNil(item.value)) as { key: string; value: number }[];
  }, [detailedScores]);

  return (
    <div className="review-summary-score flex gap-80px max-[1439px]:flex-col-reverse max-[1439px]:gap-40px">
      <div className="review-summary w-1/2 max-[1439px]:w-full">
        <div className="review-summary-title text-black font-pt-sans mb-20px">
          <span className="block m-0 text-17px mb-5px font-bold">Reviews Summary</span>
          {!hideDescription && (
            <span className="block m-0 text-gray-100 font-normal text-15px">
              Guest feedback is collected by our ground handling team at local airports as guests depart, providing
              authentic insights into their experiences. We also gather reviews from our trade partners in the form of
              written reports. All feedback is then summarised by AI to provide an overview of recent impressions and
              trends. Guest reviews are anonymous, and no personal details are disclosed
            </span>
          )}
        </div>
        <div className="review-summary-content text-black text-15px leading-24px font-pt-sans whitespace-pre-line">
          <SeeMoreTextCollapsible
            className="review-summary-content"
            maxLines={summaryMaxLines}
            expandText="Show more"
            collapseText="Show less"
            ctaClassName="!text-15px"
          >
            {summaryText}
          </SeeMoreTextCollapsible>
        </div>
      </div>
      <div className="review-score w-1/2 score-container flex flex-col items-center gap-40px mx-auto max-[1439px]:w-full">
        <div className="flex flex-col items-center gap-6px">
          <span className="font-pt-sans text-[12px] text-black tracking-[0.48px] uppercase">TOTAL SCORE</span>
          {generalScore && <span className="score font-pt-sans text-[32px] text-black">{generalScore.toFixed(1)}</span>}
          <Rating
            size={27}
            initialValue={generalScore ? Math.round((generalScore / 5) * 10) / 2 : undefined}
            transition
            readonly={true}
            allowFraction={true}
            iconsCount={5}
          />
        </div>
        <div className="detailed-categories mt-16px w-full">
          <div className="grid grid-cols-2 max-[768px]:grid-cols-1 w-full">
            {displayedScores.map(({ key, value }, index) => (
              <div
                key={key}
                className={classNames('category-item flex items-center gap-10px border-gray-20 h-25px', {
                  'border-r-1 pr-40px max-[768px]:pr-0': index % 2 === 0,
                  'border-l-1 pl-40px max-[768px]:pl-0': index % 2 === 1,
                })}
              >
                <span className="text-13px text-black font-pt-sans w-[35%] flex items-center gap-10px">
                  <SvgIcon IconComponent={scoreLabels[key]?.icon} className="w-16px h-16px" />
                  {scoreLabels[key]?.label || key}
                </span>
                <div className="flex-1 w-[55%]">
                  <ProgressBar score={(value as number) || 0} total={5} />
                </div>
                <span className="text-13px text-black font-pt-sans text-right w-[10%]">{value.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
